import { Box, Card, Stack, Typography } from '@mui/material';
import { computed } from '@preact/signals-react';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorNotification, Notification } from '../../../common/utils/notifications/Notification';
import { TestValueWrapper } from '../../../common/utils/TestValueWrapper';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { BatchExport, ExportType } from '../../../integrations/exports/models/BatchExport';
import { BatchExportRepository } from '../../../integrations/exports/services/BatchExportRepository';
import { JiraExport } from '../../../integrations/jira/models/JiraExport';
import { ProjectJira } from '../../../integrations/jira/models/ProjectJira';
import { JiraRepository } from '../../../integrations/jira/services/JiraRepository';
import { ServiceNowIntegration } from '../../../integrations/service-now/models/ServiceNowIntegration';
import { ServiceNowRepository } from '../../../integrations/service-now/services/ServiceNowRepository';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { ReportExportOptions } from '../../options/ReportExportOptions';
import { CsvExportManager } from '../../services/CsvExportManager';
import { PrintExportManager } from '../../services/PrintExportManager';
import { ExportFormat, ReportExportState } from '../states/ReportExportState';
import { ExportDrawerChips, ExportsDrawer } from './components/ExportsDrawer';
import { CsvSelector } from './components/selectors/CsvSelector';
import { JiraSelector } from './components/selectors/JiraSelector';
import { PrintSelector } from './components/selectors/PrintSelector';
import { ServiceNowSelector } from './components/selectors/ServiceNowSelector';

// Interval (in ms) for refreshing export data
const EXPORT_REFRESH_INTERVAL = 5000;

type Props = {
  onNotifications: (n: Notification[]) => void;
  onError: (e: unknown) => void;
};

export const EXPORT_CONFIRMATION_VALUE = 50;

export const ReportExportDrawer = ({ onNotifications, onError }: Props) => {
  const state = ReportExportState.getInstance();
  const [modalManager] = useState(new ModalManager());
  const projectId = state.project.value?.getId();
  const currentReport = state.peek().report;
  const [printManager] = useState(new PrintExportManager());
  const [csvManager] = useState(new CsvExportManager());
  const [jiraRepository] = useState(new JiraRepository());
  const [serviceNowRepository] = useState(new ServiceNowRepository());
  const [exportsRepository] = useState(new BatchExportRepository());
  const [integrationsRepository] = useState(new IntegrationsRepository());

  const [instanceExports, setInstanceExports] = useState<BatchExport[]>([]);

  const [isExportCheckerRunning, setIsExportCheckerRunning] = useState(false);
  const exportCheckerIntervalRef = useRef<NodeJS.Timeout | string | number | undefined>(undefined);

  const [pending, setPending] = useState(false);
  const [jira, setJira] = useState<JiraExport | null>(null);
  const [projectJira, setProjectJira] = useState<ProjectJira | null>(null);
  const [serviceNow, setServiceNow] = useState<ServiceNowIntegration | null>(null);

  const chips = computed<ExportDrawerChips[]>(() => [
    { title: t('report'), chips: [state.report.value?.getName() ?? ''] },
    { title: t('project'), chips: [state.project.value?.getName() ?? ''] },
    { title: t('profile'), chips: [state.profile.value?.getLabel() ?? t('noProfileInReport')] },
  ]);

  const instanceCount = state.getInstanceCount();
  const isJiraBatchExportEnabled = !!getFeatureFlag(FeatureFlagsType.ENABLE_JIRA_BATCH_EXPORT);
  const canMultiSelectExport = isJiraBatchExportEnabled && instanceCount > 0;

  useEffect(() => {
    if (!currentReport) {
      return;
    }
    // set exports and start interval checker in case we have exports in progress
    const loadExportsAndStartChecker = async () => {
      const exportJobs = await exportsRepository.queryExports(currentReport.getId());
      const hasRunningExports = exportJobs.some((ej) => ej.isRunning());
      if (hasRunningExports) {
        setInstanceExports(exportJobs);
        startExportChecker();
      }
    };

    loadExportsAndStartChecker();

    return () => {
      clearInterval(exportCheckerIntervalRef.current);
      exportCheckerIntervalRef.current = undefined;
    };
    // we depend only on the current report change otherwise logic is covered with "interval checks" and user actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReport]);

  useEffect(() => {
    const load = async () => {
      const [projectJira, serviceNowIntegration, integrations] = await Promise.all([
        projectId ? jiraRepository.getProjectJira(projectId) : null,
        serviceNowRepository.getIntegration(),
        integrationsRepository.getIntegrations(),
      ]);
      if (projectJira?.getProjectKey() && projectJira?.getIssueTypeKey()) {
        setProjectJira(projectJira);
      }
      // Jira integration must be fully configured which is only true if the access token, project key, and issue type are set
      const jiraIntegration = integrations.getJiraExport();
      if (jiraIntegration) {
        setJira(jiraIntegration);
      }

      setServiceNow(serviceNowIntegration);
    };

    load();

    return () => {
      integrationsRepository.cancelAllRequests();
      jiraRepository.cancelAllRequests();
      serviceNowRepository.cancelAllRequests();
      clearInterval(exportCheckerIntervalRef.current);
      setInstanceExports([]);
    };
  }, [jiraRepository, projectId, serviceNowRepository, integrationsRepository]);

  const isExportEnabled = useMemo(() => {
    if (!canMultiSelectExport) {
      return true;
    }
    if (canMultiSelectExport) {
      // if Jira is configured, export is allowed
      if (jira || projectJira) {
        return true;
      }
      // if Jira is not configured but SN is configured, export is allowed
      if (!!getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW) && serviceNow) {
        state.setFormat(ExportFormat.SERVICE_NOW);
        return true;
      }
    }
    return false;
  }, [canMultiSelectExport, jira, projectJira, serviceNow, state]);

  const refreshExports = async () => {
    if (!currentReport) {
      return;
    }
    // fetch data about running exports
    const exportJobs = await exportsRepository.queryExports(currentReport.getId());
    setInstanceExports(exportJobs);
    const allDone = exportJobs.every((ej) => ej.isFinished());
    if (allDone) {
      // stop pinging backend since we know all exports are done
      stopExportChecker();
    }
  };

  const startExportChecker = () => {
    if (isExportCheckerRunning) {
      return;
    }
    if (exportCheckerIntervalRef.current) {
      clearInterval(exportCheckerIntervalRef.current);
    }
    exportCheckerIntervalRef.current = setInterval(async () => {
      await refreshExports();
    }, TestValueWrapper.wrap(EXPORT_REFRESH_INTERVAL, 200));
    setIsExportCheckerRunning(true);
  };

  const stopExportChecker = () => {
    clearInterval(exportCheckerIntervalRef.current);
    exportCheckerIntervalRef.current = undefined;
    setIsExportCheckerRunning(false);
  };

  const onExportClick = async () => {
    const { report, includeFilteredRules, format } = state.peek();

    if (!report) return;

    const exportOptions: ReportExportOptions = {
      includeFilteredRules,
    };

    try {
      setPending(true);
      if (instanceCount > EXPORT_CONFIRMATION_VALUE) {
        const { confirmExport } = await modalManager.confirmExport(instanceCount);
        if (!confirmExport) {
          setPending(false);
          return;
        }
      }
      switch (format) {
        case ExportFormat.PRINT:
          await printManager.exportReportSummary(report.getId(), report.getName(), exportOptions);
          setPending(false);
          break;
        case ExportFormat.CSV:
          await csvManager.exportReport(report.getId(), report.getName(), exportOptions);
          setPending(false);
          break;
        case ExportFormat.JIRA: {
          const projectKey = projectJira ? projectJira.getProjectKey() : jira?.getJiraProjectKey();
          const issueTypeKey = projectJira ? projectJira.getIssueTypeKey() : jira?.getJiraIssuetypeKey();
          if (projectKey && issueTypeKey) {
            const testConnectionResult = await jiraRepository.checkJiraExport(projectKey, issueTypeKey);
            if (!testConnectionResult.isSuccessful()) {
              const message = testConnectionResult.getReason() ?? t('integrationList.jiraExport.testFail');
              onNotifications([new ErrorNotification(t('jiraExportFailure', { error: message }))]);
              setPending(false);
              return;
            }
          }

          if (!isJiraBatchExportEnabled) {
            const exportResponse = await jiraRepository.exportReport(report.getId(), includeFilteredRules);
            // handle error
            onNotifications([exportResponse.getMessage()]);
            setPending(false);
            return;
          }

          await jiraRepository.batchExport(report.getId(), [], state.getSelectedInstanceIds(), includeFilteredRules);
          await refreshExports();
          setPending(false);
          startExportChecker();
          break;
        }
        case ExportFormat.SERVICE_NOW:
          await serviceNowRepository.batchExport(report.getId(), [], state.getSelectedInstanceIds(), includeFilteredRules);
          await refreshExports();
          setPending(false);
          startExportChecker();
          break;
      }
    } catch (e: unknown) {
      setPending(false);
      onError(e);
    }
  };

  const handleCancelBatchExport = async (exportType: ExportType) => {
    if (!currentReport) {
      return;
    }
    let canceledExport: BatchExport;
    switch (exportType) {
      case ExportType.JIRA:
        canceledExport = await jiraRepository.cancelBatchExport(currentReport.getId());
        break;
      case ExportType.SERVICE_NOW:
        canceledExport = await serviceNowRepository.cancelBatchExport(currentReport.getId());
        break;
    }

    setInstanceExports(
      instanceExports.map((instanceExport) => {
        if (instanceExport.getType() === exportType) {
          // replace canceled export in the list of all exports
          return canceledExport;
        }
        return instanceExport;
      })
    );
  };

  const buttonText = () => {
    switch (state.getFormat()) {
      case ExportFormat.PRINT:
        return t('print');
      case ExportFormat.JIRA:
      case ExportFormat.SERVICE_NOW:
        return t('export');
      default:
        return t('download');
    }
  };

  const optionalSelectors = () => {
    const selectors = [];
    if (getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW)) {
      // Add ServiceNow to render list if it is enabled
      selectors.push(
        <ServiceNowSelector
          key="service-now"
          selected={state.getFormat() === ExportFormat.SERVICE_NOW}
          selectedInstancesCount={instanceCount}
          loading={pending}
          disabled={!serviceNow}
          onSelected={() => state.setFormat(ExportFormat.SERVICE_NOW)}
          exportJob={getExport(ExportType.SERVICE_NOW, instanceExports)}
          onCancel={handleCancelBatchExport}
        />
      );
    }
    return selectors;
  };

  const getExport = (exportType: ExportType, instanceExports: BatchExport[]): BatchExport | null => {
    const batchExport = instanceExports.find((br) => br.getType() === exportType);
    if (batchExport) {
      return batchExport;
    }
    return null;
  };

  return (
    <ExportsDrawer
      title={canMultiSelectExport ? t('exportTitles.manualSelection') : t('exportTitles.report')}
      triggerLabel={canMultiSelectExport ? t('exportWithCount', { count: instanceCount }) : undefined}
      disabled={!state.report.value || pending}
      chips={chips.value}
      selectors={[
        <PrintSelector
          key="print"
          description={canMultiSelectExport ? t('exportFormats.disabled', { type: 'print' }) : t('exportFormats.print.report')}
          selected={state.format.value === ExportFormat.PRINT}
          loading={pending}
          disabled={canMultiSelectExport}
          onSelected={() => state.setFormat(ExportFormat.PRINT)}
        />,
        <CsvSelector
          key="csv"
          description={canMultiSelectExport ? t('exportFormats.disabled', { type: 'CSV' }) : t('exportFormats.csv.report')}
          selected={state.format.value === ExportFormat.CSV}
          loading={pending}
          disabled={canMultiSelectExport}
          onSelected={() => state.setFormat(ExportFormat.CSV)}
        />,
        <JiraSelector
          key="jira"
          batchExportsEnabled={isJiraBatchExportEnabled}
          selectedInstancesCount={instanceCount}
          selected={state.format.value === ExportFormat.JIRA}
          loading={pending}
          disabled={!jira && !projectJira}
          exportJob={getExport(ExportType.JIRA, instanceExports)}
          onSelected={() => state.setFormat(ExportFormat.JIRA)}
          onCancel={handleCancelBatchExport}
        />,
        ...optionalSelectors(),
      ]}
      actionButtonProps={{
        children: buttonText(),
        onClick: onExportClick,
        disabled: !state.report.value || pending || !isExportEnabled,
        loading: pending,
      }}
      additionalContent={
        canMultiSelectExport && (
          <Box>
            <Typography variant="overline">{t('includes')}:</Typography>
            <Stack direction={'row'} spacing={2} sx={{ height: '5rem', alignItems: 'center' }}>
              <Card data-role="export-drawer-instances" sx={{ flex: 1, p: 1.5 }}>
                <Typography>{t('exportDrawer.instances')}</Typography>
                <Typography fontSize={'1.625rem'} fontWeight={'500'}>
                  {state.getInstanceCount()}
                </Typography>
              </Card>
              <Typography>{t('from')}</Typography>
              <Card data-role="export-drawer-issues" sx={{ flex: 1, p: 1.5 }}>
                <Typography>{t('exportDrawer.issues')}</Typography>
                <Typography fontSize={'1.625rem'} fontWeight={'500'}>
                  {state.getIssueCount()}
                </Typography>
              </Card>
            </Stack>
          </Box>
        )
      }
    />
  );
};
